import { useCallback, useRef, useState } from 'react'
import { debounce } from 'debounce'
import { useCombobox } from 'downshift'
import { useFormikContext } from 'formik'
import { useToast } from '@sketch/toasts'

import {
  WorkspaceMembershipFragment,
  useGetInvitableUsersLazyQuery,
} from '@sketch/gql-types'
import { useFlag } from 'containers/flags'

interface UseAutocompleteProps {
  shareIdentifier?: string
  projectIdentifier?: string
  workspaceIdentifier: string
  membersList: string[]
  itemToString?: (item: WorkspaceMembershipFragment | null) => string
  options?: {
    userIdentifier?: string
  }
}

export interface FormValues {
  email: string
  workspaceMembershipIdentifier: string
  shouldValidateEmail: boolean
  guestCommentsEnabled: boolean
  guestInspectEnabled: boolean
}

/**
 * Hook to orchestrate all the logic to create a users autocomplete
 */
const useAutocomplete = function useAutoComplete({
  projectIdentifier,
  workspaceIdentifier,
  membersList,
  itemToString = (item: WorkspaceMembershipFragment | null) =>
    item?.user?.name || '',
  options,
}: UseAutocompleteProps) {
  const { showToast } = useToast()
  const [items, setItems] = useState<WorkspaceMembershipFragment[]>([])

  const canAddGuestsToProjects = useFlag('guests-in-project')

  const formik = useFormikContext<FormValues>()
  const { setValues } = formik

  const [getInvitableUsers] = useGetInvitableUsersLazyQuery({
    fetchPolicy: 'cache-and-network',
    onCompleted(data) {
      // Exclude members that have been already invited from dropdown
      const entries = data?.invitableUsers?.entries || []
      const memberIdentifiers = new Set(membersList)

      const invitableUsers = entries.filter(
        entry =>
          !memberIdentifiers.has(entry.identifier) &&
          // This check removes current user from invitable users
          // when creating a new project and inviting users
          entry.user?.identifier !== options?.userIdentifier
      ) as WorkspaceMembershipFragment[]

      setItems(invitableUsers)
    },
    onError() {
      showToast('Failed to autocomplete name.', 'negative')
    },
  })

  const getAutoCompleteItems = function (inputValue: string) {
    const isValueAMention = inputValue.charAt(0) === '@'
    const isValueTooShort = inputValue.length === 0
    const isValueEmpty = inputValue.trim() === ''

    const search = isValueAMention ? inputValue.substring(1) : inputValue
    const skip = isValueTooShort || isValueEmpty

    if (skip) {
      setItems([])
      return
    }

    getInvitableUsers({
      variables: {
        projectIdentifier,
        workspaceIdentifier,
        search,
      },
    })
  }

  // Debounced function needs to be kept in ref element to
  // avoid unexpected re-renders when used within useCallback
  const debouncedFn = debounce(getAutoCompleteItems, 300)
  const debounceGetAutocompleteItemsFn = useRef<typeof debouncedFn | null>(null)
  if (!debounceGetAutocompleteItemsFn.current) {
    debounceGetAutocompleteItemsFn.current = debouncedFn
  }

  const otherValues = formik.values

  const onInputValueChange = useCallback(
    ({
      inputValue,
      selectedItem,
    }: {
      inputValue?: string
      selectedItem?: WorkspaceMembershipFragment | null
    }) => {
      const userHasNotSelectedValue = selectedItem?.user?.name !== inputValue
      setValues({
        ...otherValues,

        // When the field value does not match the combobox selection
        // then we indicate to formik we should be treating it as a plain email value
        shouldValidateEmail: userHasNotSelectedValue,

        // Set the workspaceMembershipIdentifier if we are using value from the autocomplete.
        // Otherwise, we will send the plain email
        workspaceMembershipIdentifier: selectedItem?.identifier || '',

        // Set the formik value used to make the mutation to either combobox selection
        // or the input plain value, depending on whether a combobox item has been selected
        email:
          canAddGuestsToProjects && userHasNotSelectedValue
            ? selectedItem?.user?.email || inputValue || ''
            : '',
      })

      if (!inputValue) {
        setItems([])
        return
      }

      const valueHasChanged = selectedItem?.user?.name !== inputValue
      const getElementsFn = debounceGetAutocompleteItemsFn.current
      if (valueHasChanged && getElementsFn) {
        getElementsFn(inputValue)
      }
    },
    [
      canAddGuestsToProjects,
      debounceGetAutocompleteItemsFn,
      setValues,
      otherValues,
    ]
  )

  const combobox = useCombobox({
    items,
    itemToString,
    onInputValueChange,
  })

  const { inputValue: comboInputValue, selectedItem } = combobox

  return {
    items,
    formik,
    combobox,
    valueSelected: selectedItem?.user?.name || '',
    isValueSelected: selectedItem?.user?.name === comboInputValue,
  }
}

export { useAutocomplete }
